import type { DefaultRootState } from "react-redux";
import type { Dispatch } from "redux";
import type { ThunkAction } from "redux-thunk";
import { setToolbarSnack } from "actions/context";
import {
  type ICreateMerchantUserAttributes,
  type IMerchantUser,
  getAllRoles,
  postCreateMerchantUser,
} from "interfaces/users/analytics";

import { RequestStatus } from "types/request";
import type { IRole } from "types/user";

export type AnalyticsUserActionTypes =
  | ISetAnalyticsRequestStatusAction
  | IFetchRolesAction
  | IUpdateLocalUserRecordWithNewMerchantUserAction;

type ThunkResult<R> = ThunkAction<
  R,
  DefaultRootState,
  undefined,
  AnalyticsUserActionTypes
>;

export enum TypeKeys {
  SET_ANALYTICS_REQUEST_STATUS_ACTION = "USERS/ANALYTICS/SET_ANALYTICS_REQUEST_STATUS_ACTION",
  FETCH_ROLES_ACTION = "USERS/ANALYTICS/FETCH_ROLES_ACTION",
  UPDATE_LOCAL_USER_RECORD_WITH_NEW_MERCHANT_USER = "USERS/ANALYTICS/UPDATE_LOCAL_USER_RECORD_WITH_NEW_MERCHANT_USER",
}

export enum AnalyticsRequestKey {
  FetchRoles = "FetchRoles",
  CreateMerchantUser = "CreateMerchantUser",
}

export interface ISetAnalyticsRequestStatusAction {
  type: TypeKeys.SET_ANALYTICS_REQUEST_STATUS_ACTION;
  requestKey: AnalyticsRequestKey;
  requestStatus: RequestStatus;
}

export interface IFetchRolesAction {
  type: TypeKeys.FETCH_ROLES_ACTION;
  roles: IRole[];
}

const setAnalyticsRequestStatus = (
  requestKey: AnalyticsRequestKey,
  requestStatus: RequestStatus
): ISetAnalyticsRequestStatusAction => {
  return {
    type: TypeKeys.SET_ANALYTICS_REQUEST_STATUS_ACTION,
    requestKey,
    requestStatus,
  };
};

export const fetchRoles = (brandPrettyUrl: string): ThunkResult<void> => {
  return (dispatch: Dispatch<any>): Promise<void> => {
    dispatch(
      setAnalyticsRequestStatus(
        AnalyticsRequestKey.FetchRoles,
        RequestStatus.REQUESTING
      )
    );

    return getAllRoles(brandPrettyUrl)
      .then((roles) => {
        dispatch(
          setAnalyticsRequestStatus(
            AnalyticsRequestKey.FetchRoles,
            RequestStatus.SUCCESS
          )
        );

        dispatch({
          type: TypeKeys.FETCH_ROLES_ACTION,
          roles,
        });
      })
      .catch(() => {
        dispatch(
          setAnalyticsRequestStatus(
            AnalyticsRequestKey.FetchRoles,
            RequestStatus.FAILURE
          )
        );
      });
  };
};

export const createMerchantUser = (
  brandPrettyUrl: string,
  attrs: ICreateMerchantUserAttributes
): ThunkResult<void> => {
  return (dispatch: Dispatch<any>): Promise<void> => {
    dispatch(
      setAnalyticsRequestStatus(
        AnalyticsRequestKey.CreateMerchantUser,
        RequestStatus.REQUESTING
      )
    );

    return postCreateMerchantUser(brandPrettyUrl, attrs)
      .then((merchantUser) => {
        dispatch(
          setAnalyticsRequestStatus(
            AnalyticsRequestKey.CreateMerchantUser,
            RequestStatus.SUCCESS
          )
        );

        dispatch(updateLocalUserRecordWithNewMerchantUser(merchantUser));
        dispatch(
          setToolbarSnack({
            id: "create_analytics_user_status",
            type: "success",
            message: `${merchantUser.firstName} ${
              merchantUser.lastName
            } has been assigned a Tools & Analytics role.`,
          })
        );
      })
      .catch(() => {
        dispatch(
          setToolbarSnack({
            id: "create_analytics_user_status",
            type: "error",
            message:
              "There was an error assigning this user a Tools & Analytics role.",
          })
        );
      });
  };
};

export interface IUpdateLocalUserRecordWithNewMerchantUserAction {
  type: TypeKeys.UPDATE_LOCAL_USER_RECORD_WITH_NEW_MERCHANT_USER;
  merchantUser: IMerchantUser;
}

export function updateLocalUserRecordWithNewMerchantUser(
  merchantUser: IMerchantUser
): IUpdateLocalUserRecordWithNewMerchantUserAction {
  return {
    type: TypeKeys.UPDATE_LOCAL_USER_RECORD_WITH_NEW_MERCHANT_USER,
    merchantUser,
  };
}
